import memoize from 'lodash/memoize'
import { Position } from 'src/common-services'
import { toBounds, toLatLng, toLatLngBounds, toPosition } from 'src/utils/map'
import { Bounds } from 'src/types/map'
import { GOOGLE_API_KEY } from 'src/components/map/google-map/loader'
import { USE_LOCAL_CACHE, fakeImage, localCacheGet, localCacheKey, localCacheSet } from 'src/utils/local-cache'
import { browserLogger } from 'src/utils/browser-logger'
import PromiseQueue from 'promise-queue'

type GooglePlaceGeometry = {
  location: Position
  viewport: Bounds
}

export type GooglePlace = {
  place_id: string
  name: string
  // https://developers.google.com/maps/documentation/places/web-service/supported_types
  types: string[]
  formatted_address: string
  address_components: google.maps.GeocoderAddressComponent[]
  phone: string
  rating: number
  user_ratings_total: number
  price_level: number
  geometry: GooglePlaceGeometry
  photos: google.maps.places.PlacePhoto[]
  website: string
  streetViewThumbnailUrl: string
}

// memoize this so we don't make a ton of divs
const getService = memoize(() => {
  const div = document.createElement('div')
  const placesService = new google.maps.places.PlacesService(div)
  return placesService
})

const basicFields = ['name', 'formatted_address', 'place_id', 'geometry']

// you can get a list of all fields here
// https://developers.google.com/maps/documentation/javascript/places#place_details
const fields = basicFields.concat([
  'rating',
  'type',
  'address_components',
  'formatted_phone_number',
  'user_ratings_total',
  'price_level',
  'photo',
  'website',
])

const emptyGeometry = (): GooglePlaceGeometry => {
  return {
    location: [0, 0],
    viewport: {
      center: [0, 0],
      northEast: [0, 0],
      southWest: [0, 0],
    },
  }
}

const missingGeometry = emptyGeometry()

const toGooglePlaceGeometry = (rawPlace: google.maps.places.PlaceGeometry | undefined): GooglePlaceGeometry => {
  if (!rawPlace) {
    return missingGeometry
  }
  const { location, viewport } = rawPlace
  const result: GooglePlaceGeometry = emptyGeometry()
  if (location) {
    result.location = toPosition(location)
  }
  if (viewport) {
    result.viewport = toBounds(viewport)
  }
  return result
}

const toGooglePlace = (rawPlace: google.maps.places.PlaceResult): GooglePlace => {
  const geometry = toGooglePlaceGeometry(rawPlace.geometry)
  return {
    place_id: rawPlace.place_id ?? '',
    name: rawPlace.name ?? '',
    formatted_address: rawPlace.formatted_address ?? '',
    rating: rawPlace.rating ?? 0,
    user_ratings_total: rawPlace.user_ratings_total ?? 0,
    price_level: rawPlace.price_level ?? 0,
    types: rawPlace.types ?? [],
    photos: rawPlace.photos ?? [],
    phone: rawPlace.formatted_phone_number ?? '',
    website: rawPlace.website ?? '',
    address_components: rawPlace.address_components || [],
    geometry,
    streetViewThumbnailUrl: getStreetViewThumbnailUrl(geometry.location, {
      width: 800,
      height: 800,
    }),
  }
}

// Add functions to mock the Google Place Object
const rehydrateGooglePlace = (place: GooglePlace | null | undefined) => {
  if (place === undefined || place === null) {
    return place
  }
  return {
    ...place,
    // @ts-ignore
    formatted_address: place.formatted_address || place.formattedd_address || '',
    photos: place.photos.map((photo) => {
      return {
        ...photo,
        getUrl: () => fakeImage(photo.width, photo.height),
      }
    }),
  }
}

const placeDetailsCache: Record<GooglePlace['place_id'], GooglePlace> = {}

/**
 * Get place details from google
 * @param placeId The placeId to look up from google
 * @param basic Whether to only have basic fields returned - cheaper and useful when not showing this place on the UI
 * @returns A google place or undefined if one cannot be found
 */
const findPlaceDetailsQuence = new PromiseQueue(2, Infinity)
export const findPlaceDetails = async (
  placeId: string,
  basic: boolean = false
): Promise<GooglePlace | null | undefined> => {
  browserLogger.info('findPlaceDetails', { placeId, basic })
  if (placeDetailsCache[placeId]) {
    return placeDetailsCache[placeId]
  }
  const localCacheQuery = localCacheKey('findPlaceDetails', { placeId, basic })
  if (USE_LOCAL_CACHE) {
    const cache = await localCacheGet(localCacheQuery)
    if (cache) {
      return rehydrateGooglePlace(cache)
    }
  }
  // Implemented a queue to prevent too many requests to google OVER_QUERY_LIMIT
  const response: GooglePlace | null | undefined = await findPlaceDetailsQuence.add(() => {
    return new Promise((resolve, reject) => {
      getService().getDetails(
        { placeId, fields: basic ? basicFields : fields },
        (res: google.maps.places.PlaceResult | null, status: google.maps.places.PlacesServiceStatus) => {
          if (status === google.maps.places.PlacesServiceStatus.INVALID_REQUEST) {
            return resolve(null)
          }
          if (status === google.maps.places.PlacesServiceStatus.NOT_FOUND) {
            if (res?.place_id) {
              return resolve(toGooglePlace(res))
            } else {
              return resolve(null)
            }
          }
          if (status !== google.maps.places.PlacesServiceStatus.OK) {
            const statusString = status as string
            const err = new Error(`Unexpected result from places service: ${statusString}`)
            reject(err)
          }
          if (res == null) {
            return resolve(undefined)
          }
          const placeResult = toGooglePlace(res)
          placeDetailsCache[placeId] = placeResult
          return resolve(placeResult)
        }
      )
    })
  })
  localCacheSet(localCacheQuery, response)
  return response
}

export const findPlace = async (searchText: string, center: Position): Promise<GooglePlace | undefined> => {
  const localCacheQuery = localCacheKey('findPlace', { searchText })
  if (USE_LOCAL_CACHE) {
    const cache = await localCacheGet(localCacheQuery)
    const rehydrated = rehydrateGooglePlace(cache)
    if (rehydrated) {
      return rehydrated
    }
  }
  const response: GooglePlace | undefined = await new Promise((resolve, reject) => {
    const locationBias = new google.maps.LatLng({ lng: center[0], lat: center[1] })
    getService().findPlaceFromQuery(
      { fields: basicFields, query: searchText, locationBias },
      (res: google.maps.places.PlaceResult[] | null, status: google.maps.places.PlacesServiceStatus) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK) {
          if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            browserLogger.warn('no search results match', searchText)
            return resolve(undefined)
          }
          const statusString = status as string
          const err = new Error(`Unexpected result from places service: ${statusString}`)
          reject(err)
        }
        if (res == null || !res.length) {
          return resolve(undefined)
        }
        return resolve(toGooglePlace(res[0]))
      }
    )
  })
  localCacheSet(localCacheQuery, response)
  return response
}

type PlaceSearchRequest = {
  searchText: string
  bounds: Bounds
}

type TextSearchRequest = {
  radiusMeters: number
  center: Position
  searchText: string
}

export const geocode = async (request: google.maps.GeocoderRequest): Promise<google.maps.GeocoderResult[]> => {
  const res = await new Promise<google.maps.GeocoderResult[]>(async (resolve, reject) => {
    const geocoder = new google.maps.Geocoder()

    await geocoder.geocode(request, (results, status) => {
      if (status !== google.maps.GeocoderStatus.OK) {
        if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
          browserLogger.warn('no search results match', { request })
          return resolve([])
        }
        const err = Error(`Unable to get geocode results. Server returned ` + (status as string))
        return reject(err)
      }

      resolve(results ? results : [])
    })
  })

  return res
}

export const textSearch = async (request: TextSearchRequest): Promise<GooglePlace[]> => {
  const response: GooglePlace[] = await new Promise((resolve, reject) => {
    const req: any = {
      radius: request.radiusMeters,
      location: toLatLng(request.center),
      query: request.searchText,
    }
    getService().textSearch(req, (places: google.maps.places.PlaceResult[] | null, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK) {
        if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          browserLogger.warn('no search results match', { request })
          return resolve([])
        }
        const err = Error(`Unable to search for places. Server returned ` + (status as string))
        return reject(err)
      }
      const mapped = (places || []).map(toGooglePlace)
      resolve(mapped)
    })
  })
  return response
}

export const searchForPlaces = async (request: PlaceSearchRequest): Promise<GooglePlace[]> => {
  const localCacheQuery = localCacheKey('searchForPlaces', {
    searchText: request.searchText + JSON.stringify(request.bounds),
  })
  if (USE_LOCAL_CACHE) {
    const cache: GooglePlace[] | undefined = await localCacheGet(localCacheQuery)
    if (cache) {
      return cache.flatMap((place) => {
        const rehydrated = rehydrateGooglePlace(place)
        if (rehydrated) {
          return rehydrated
        }
        return []
      })
    }
  }
  const response: GooglePlace[] = await new Promise((resolve, reject) => {
    const req: google.maps.places.TextSearchRequest = {
      bounds: toLatLngBounds(request.bounds),
      query: request.searchText,
    }
    getService().textSearch(req, (places: google.maps.places.PlaceResult[] | null, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK) {
        if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          browserLogger.warn('no search results match', { request })
          return resolve([])
        }
        const err = Error(`Unable to search for places. Server returned ` + (status as string))
        return reject(err)
      }
      const mapped = (places || []).map(toGooglePlace)
      resolve(mapped)
    })
  })
  localCacheSet(localCacheQuery, response)
  return response
}

const autocompleteService = memoize(() => new google.maps.places.AutocompleteService())

type CompleteRequest = {
  input: string
  bounds: Bounds
}

type QuerySearchResult = {
  description: string
  structured_formatting: {
    main_text: string
    secondary_text: string
    main_text_matched_substrings: [
      {
        offset: number
        length: number
      },
    ]
  }
}

type QueryPlaceResult = QuerySearchResult & {
  place_id: string
  types: string[]
}

export const isPlaceResult = (result: QueryResult): result is QueryPlaceResult => {
  return Object.prototype.hasOwnProperty.call(result, 'place_id')
}

// An auto-complete query can return either a matched place or just a search string
// you can pass to the place search service to find places matching that search string
export type QueryResult = QuerySearchResult | QueryPlaceResult

export const getQueryPredictions = async (request: CompleteRequest): Promise<QueryResult[]> => {
  const service = autocompleteService()

  const localCacheQuery = localCacheKey('getQueryPredictions', { input: request.input })
  if (USE_LOCAL_CACHE) {
    const cache = await localCacheGet(localCacheQuery)
    if (cache) {
      return cache
    }
  }
  const response: QueryResult[] = await new Promise((resolve, reject) => {
    const req: google.maps.places.QueryAutocompletionRequest = {
      input: request.input,
      bounds: toLatLngBounds(request.bounds),
    }
    service.getQueryPredictions(req, (results, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK) {
        if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          browserLogger.warn('no place predictions for', { request })
          return resolve([])
        }
        const err = Error(`Unable to get autocomplete query. Server returned ` + (status as string))
        return reject(err)
      }
      for (const result of results || []) {
        const isNonPlace = result.place_id == null
        if (isNonPlace) {
          const mainText = result.description
          for (const other of results || []) {
            // try to match place results with the exact same main text, meaning there's a venu
            if (other.place_id == null) {
              continue
            }
            const otherMainText: string = (other as any).structured_formatting?.main_text ?? ''
            if (otherMainText && otherMainText.toLowerCase() === mainText.toLowerCase()) {
              const thing = result as any
              thing.structured_formatting.secondary_text = 'see locations'
            }
          }
        }
      }
      // TODO(bmc): the type definitions seem to be wrong for the return value from getQueryPredictions...
      // the return values do have structured_formatting both in practice & according to the documenation
      // though the typedefs claim they do not.
      // https://developers.google.com/maps/documentation/javascript/places-autocomplete#place_autocomplete_service
      resolve((results as any) ?? [])
    })
  })
  localCacheSet(localCacheQuery, response)
  return response
}

export const getStreetViewThumbnailUrl = (
  coordinates: number[],
  dimensions: {
    width: number
    height: number
  }
) => {
  const { width, height } = dimensions
  if (USE_LOCAL_CACHE) {
    return fakeImage(width, height)
  }
  const parameters = `?size=${width}x${height}&location=${coordinates[1]},${coordinates[0]}&key=${GOOGLE_API_KEY}`
  return `https://maps.googleapis.com/maps/api/streetview${parameters}`
}

export const getPlaceStateSortName = (place: GooglePlace): string | null => {
  if (!place.address_components) {
    return null
  }
  const state = place.address_components.find((component) => component.types.includes('administrative_area_level_1'))
  return state?.short_name ?? null
}
